type Hand = "L" | "R";
type Size = "sm" | "md" | "lg";

type NoteTileProps = {
  readonly name: string;
  readonly hand: Hand;
  readonly size?: Size;
  readonly dim?: boolean;
  /**
   * The key is down right now: fuller fill, border at full strength.
   *
   * This used to write a `data-active` attribute and nothing else — no
   * selector anywhere read it, so a caller subscribing to every press and
   * release of the piano got exactly zero pixels of difference for it.
   */
  readonly active?: boolean;
  /**
   * Briefly overlays the tile with a white border + glow. Driven by the
   * caller (typically a `setTimeout` after a successful note hit, before
   * advancing the FSM). Independent from `active` so the visual remains
   * legible even when the user is still holding the key.
   */
  readonly flash?: boolean;
};

const SIZE_PX: Record<Size, number> = {
  sm: 50,
  md: 70,
  lg: 110,
};

const RADIUS_PX: Record<Size, string> = {
  sm: "var(--radius-tile)",
  md: "var(--radius-tile)",
  lg: "18px",
};

const FONT_PX: Record<Size, number> = {
  sm: 13,
  md: 18,
  lg: 28,
};

export function NoteTile({
  name,
  hand,
  size = "md",
  dim = false,
  active = false,
  flash = false,
}: NoteTileProps) {
  const handColor =
    hand === "L" ? "var(--color-hand-left)" : "var(--color-hand-right)";
  const tileShadow =
    hand === "L" ? "var(--shadow-tile-left)" : "var(--shadow-tile-right)";

  const px = SIZE_PX[size];
  const radius = RADIUS_PX[size];
  const fontSize = FONT_PX[size];

  const baseOpacity = dim ? 0.5 : 1;
  const fillOpacity = dim ? 0.05 : active ? 0.24 : 0.12;
  const borderOpacity = dim ? 0.2 : active ? 1 : 0.5;

  const borderColor = flash
    ? "rgba(255, 255, 255, 0.95)"
    : `color-mix(in srgb, ${handColor} ${borderOpacity * 100}%, transparent)`;

  const boxShadow = flash
    ? "0 0 24px rgba(255, 255, 255, 0.55), 0 0 48px rgba(255, 255, 255, 0.25)"
    : dim
      ? "none"
      : tileShadow;

  return (
    <div
      data-active={active ? "true" : undefined}
      data-flash={flash ? "true" : undefined}
      style={{
        width: px,
        height: px,
        borderRadius: radius,
        position: "relative",
        display: "grid",
        placeItems: "center",
        background: `color-mix(in srgb, ${handColor} ${fillOpacity * 100}%, transparent)`,
        border: `1px solid ${borderColor}`,
        boxShadow,
        opacity: baseOpacity,
        transition:
          "transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.15s ease",
      }}
    >
      <span
        className="font-mono font-semibold tabular-nums"
        style={{
          fontSize,
          color: handColor,
          letterSpacing: "0.04em",
        }}
      >
        {name}
      </span>

      <span
        aria-hidden
        className="font-mono"
        style={{
          position: "absolute",
          right: 6,
          bottom: 4,
          fontSize: 9,
          lineHeight: 1,
          letterSpacing: "0.08em",
          color: handColor,
          opacity: 0.85,
        }}
      >
        {hand}
      </span>

      <span className="sr-only">
        {name}, main {hand === "L" ? "gauche" : "droite"}
      </span>
    </div>
  );
}
